'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * `.rail` from the article reference (2026-08-08): the sticky right column
 * carrying share controls above the article's own stats.
 *
 * Every stat is derived from the post — reading time and the heading count.
 * The reference also shows a per-article "flags covered" figure; there is no
 * field behind that here, so it is not invented.
 *
 * Hidden below 820px, where the reference drops the rail entirely.
 */
export default function ArticleRail({
  url,
  readingTime,
  sections,
}: {
  url: string
  readingTime?: number
  sections?: number
}) {
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<number | null>(null)

  // The reset timer must not outlive the component: navigating away inside
  // the 2s window would otherwise set state on an unmounted node.
  useEffect(
    () => () => {
      if (resetTimer.current) window.clearTimeout(resetTimer.current)
    },
    []
  )

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      if (resetTimer.current) window.clearTimeout(resetTimer.current)
      resetTimer.current = window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be blocked by permissions; the LinkedIn link and the
      // address bar both still work, so this fails quietly.
    }
  }

  return (
    <aside className='article-rail' aria-label='Article tools'>
      <div className='rail-group'>
        <span className='rail-label'>Share</span>
        <button type='button' className='share-button' onClick={copyLink}>
          {copied ? 'Link copied' : 'Copy link'}
        </button>
        <a
          className='share-button'
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
          target='_blank'
          rel='noopener noreferrer'
        >
          LinkedIn
        </a>
        <button
          type='button'
          className='share-button'
          onClick={() => window.print()}
        >
          Print
        </button>
      </div>

      {(readingTime || sections) && (
        <div>
          {readingTime && (
            <div className='rail-stat'>
              <strong>{readingTime} min</strong>
              <span>Reading time</span>
            </div>
          )}
          {sections ? (
            <div className='rail-stat'>
              <strong>{sections}</strong>
              <span>Sections</span>
            </div>
          ) : null}
        </div>
      )}
    </aside>
  )
}
